"use client";

import { GLOBAL_UTM_KEY } from "@shared/url/utm";
import { SURVEY_SESSION_KEY, forgetSessionMirror } from "./surveySession";
import type { SurveyAnswers } from "@features/survey/server/types";
import { UTM_STORAGE_KEY } from "./useUtmCapture";

export const SURVEY_STATE_KEY = "loveiq-survey-answers";
export const SURVEY_INDEX_KEY = "loveiq-survey-index";
export const SURVEY_STEP_KEY = "loveiq-survey-step";
export const PENDING_COMPLETION_KEY = "loveiq-survey-pending-completion";
/**
 * When the reader agreed on the consent screen (18+, terms, sensitive data) for the run in
 * progress. A draft is not proof of it: the homepage question card saves its answer before
 * the reader ever sees that screen, and the server stamps consent on every submission.
 */
export const SURVEY_CONSENT_KEY = "loveiq-survey-consent";
/** history.state key on an entry that is one of the questions. See SurveyEngine. */
export const QUESTION_STATE_KEY = "surveyQuestion";
/** history.state key: the question index of the base those question entries stack on. */
export const BASE_STATE_KEY = "surveyQuestionBase";
export const ANSWERS_STORAGE_KEY = SURVEY_STATE_KEY;

export interface PendingSurveyCompletion {
  sessionId: string;
  email: string;
  firstName: string;
  answers: SurveyAnswers;
  startedAt: string;
  durationMs: number;
  utmTracker: string | null;
  currentIndex: number;
  savedAt: string;
}

/**
 * The one question the landing page asks up front (the hero / closing-CTA card
 * in features/landing/ui/white/WQuestionCard.tsx). Answering it there stores a
 * real answer and marks the qId as "prefilled", so SurveyEngine drops it from
 * the flow; every other question is asked inside /survey.
 */
export const LANDING_PREFILL_QID = "01002";

function canUseStorage(): boolean {
  return typeof window !== "undefined";
}

/**
 * Persist an answer captured before the survey started, into the same blob
 * `useSurveyState` hydrates from.
 *
 * It only marks the question as prefilled for a FRESH draft. If the visitor
 * already has survey progress, the answer is still saved (so their choice is
 * never lost) but the question stays in the flow — removing a question from
 * under someone mid-survey would shift every index after it.
 */
export function saveLandingPrefill(qId: string, value: number): void {
  if (!canUseStorage()) return;
  try {
    const raw = localStorage.getItem(SURVEY_STATE_KEY);
    const prev = raw ? (JSON.parse(raw) as Record<string, unknown> | null) : null;
    const base = prev && typeof prev === "object" ? prev : {};
    const answers = (base.answers as Record<string, unknown> | undefined) ?? {};
    const prefilled = Array.isArray(base.prefilled) ? (base.prefilled as string[]) : [];
    const isFreshDraft = Object.keys(answers).length === 0 && !base.currentIndex;
    // A fresh draft is a new run, and consent belongs to a run: one given and then left
    // with no answers (or by someone else on a shared device) must not carry over to it.
    if (isFreshDraft) {
      forgetSurveyConsent();
      // Same for a session id left by an earlier run: the new draft must not adopt it.
      forgetSessionMirror();
    }

    localStorage.setItem(
      SURVEY_STATE_KEY,
      JSON.stringify({
        ...base,
        answers: { ...answers, [qId]: value },
        startedAt: (base.startedAt as string | undefined) || new Date().toISOString(),
        prefilled: isFreshDraft && !prefilled.includes(qId) ? [...prefilled, qId] : prefilled,
      })
    );
  } catch {
    // Storage unavailable (private mode / quota). The survey still works — the
    // visitor just answers this question again inside the flow.
  }
}

export function loadPendingCompletion(): PendingSurveyCompletion | null {
  if (!canUseStorage()) return null;

  try {
    const raw = localStorage.getItem(PENDING_COMPLETION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSurveyCompletion | null;
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.sessionId || !parsed.email || !parsed.startedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function savePendingCompletion(payload: PendingSurveyCompletion): void {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(PENDING_COMPLETION_KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable */
  }
}

export function clearPendingCompletion(): void {
  if (!canUseStorage()) return;
  try {
    localStorage.removeItem(PENDING_COMPLETION_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Also held in memory, so a browser that refuses storage is not sent back to consent on every Back. */
let consentGivenOnThisPage = false;

export function recordSurveyConsent(): void {
  consentGivenOnThisPage = true;
  try {
    localStorage.setItem(SURVEY_CONSENT_KEY, new Date().toISOString());
  } catch {
    /* storage unavailable: the consent screen is shown again after a reload */
  }
}

export function hasSurveyConsent(): boolean {
  if (consentGivenOnThisPage) return true;
  try {
    return Boolean(localStorage.getItem(SURVEY_CONSENT_KEY));
  } catch {
    return false;
  }
}

export function forgetSurveyConsent(): void {
  consentGivenOnThisPage = false;
  try {
    localStorage.removeItem(SURVEY_CONSENT_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Test-only: module state outlives a jsdom test. */
export function __resetSurveyConsentForTests(): void {
  consentGivenOnThisPage = false;
}

export function clearPersistedSurveyState(options?: {
  clearPendingCompletion?: boolean;
  clearSurveySession?: boolean;
}): void {
  consentGivenOnThisPage = false;
  if (!canUseStorage()) return;

  try {
    localStorage.removeItem(SURVEY_STATE_KEY);
    localStorage.removeItem(SURVEY_INDEX_KEY);
    // Consent belongs to the run it was given for; the next run asks again.
    localStorage.removeItem(SURVEY_CONSENT_KEY);
    localStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(GLOBAL_UTM_KEY);
    if (options?.clearPendingCompletion) {
      localStorage.removeItem(PENDING_COMPLETION_KEY);
    }
    sessionStorage.removeItem(SURVEY_STEP_KEY);
    // The draft's id goes with the draft, whatever happens to the tab's: kept after a
    // submit, it was adopted by the next run started from the homepage card in a new tab,
    // and the server answered that run with the finished one's submission (#375).
    forgetSessionMirror();
    if (options?.clearSurveySession !== false) {
      sessionStorage.removeItem(SURVEY_SESSION_KEY);
    }
  } catch {
    /* storage unavailable */
  }
}
